import React, { useState, useEffect } from 'react';
import {
  ConfigProvider,
  useConfig,
  ConfigProviderJapanese,
  useConfigJapanese,
} from './contexts/ConfigContext';
import { Toolbar } from './components/Toolbar';
import { Result } from './components/Result';
import { WordInfo } from './types';
import './styles/theme.css';
import './App.css';
import {
  getWordInfo,
  hasKanji,
  annotateJapaneseFromChinese,
  convertToTraditional,
  TranslateService
} from './services/japanese';
import { TranslateInput } from './components/TranslateInput';
import { Toast } from './components/Toast';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { LoadingProgress } from './components/common/LoadingProgress';
import { WordPopover } from './components/WordPopover';
import { GojuonChart } from './components/GojuonChart';
import { Settings } from './components/Settings';
import { MobileMenu } from './components/MobileMenu';
import { IconSvg } from './components/IconSvg';
import { TranslateServiceContent } from './components/TranslateServiceSelector';

// 内部组件，用于使用 hooks
const AppContent: React.FC = () => {
  const { config, setConfig, resetConfig } = useConfig();
  const { config: japaneseConfig, updateConfig: updateJapaneseConfig, resetConfig: resetJapaneseConfig } = useConfigJapanese();
  const [inputText, setInputText] = useState('');
  const [words, setWords] = useState<WordInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [traditionalText, setTraditionalText] = useState<string>('');
  const [originalText, setOriginalText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [initProgress, setInitProgress] = useState(0);
  const [initMessage, setInitMessage] = useState('');
  const [activeWord, setActiveWord] = useState<{ key: string; word: WordInfo; element: HTMLElement } | null>(null);
  const [showGojuon, setShowGojuon] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // 监听 showTraditional 的变化
  useEffect(() => {
    const updateTraditional = async () => {
      if (config.showTraditional && originalText) {
        try {
          const traditional = await convertToTraditional(originalText);
          setTraditionalText(traditional);
        } catch (error) {
          console.error('繁体转换失败:', error);
        }
      }
    };
    updateTraditional();
  }, [config.showTraditional, originalText]);

  const handleTranslate = async () => {
    if (!inputText.trim()) return;

    setLoading(true);
    setError(null);
    setShowContent(true);
    setTraditionalText('');
    setOriginalText(inputText);
    // 清空选中的词卡
    setActiveWord(null);

    try {
      // 使用新的一站式注释功能，传入进度回调
      const result = await annotateJapaneseFromChinese(
        inputText,
        japaneseConfig.translateService,
        (progress, message) => {
          setInitProgress(progress);
          setInitMessage(message);
        }
      );

      // 预先初始化数组，避免面板闪烁
      setWords(new Array(result.annotation.segments.length).fill(null));
      setLoading(false);

      // 处理每个分词的详细信息
      for (let i = 0; i < result.annotation.segments.length; i++) {
        try {
          const segment = result.annotation.segments[i];
          const wordInfo = await getWordInfo(segment);

          setWords(prev => {
            const newWords = [...prev];
            newWords[i] = wordInfo;
            return newWords;
          });
        } catch (error) {
          // 如果单个词处理失败，使用占位符
          setWords(prev => {
            const newWords = [...prev];
            newWords[i] = {
              text: result.annotation.segments[i],
              isKanjiWord: hasKanji(result.annotation.segments[i]),
              kanjis: [],
              romaji: result.annotation.romaji[i] || '...',
              error: true
            };
            return newWords;
          });
        }
      }

      // 异步获取繁体中文
      if (config.showTraditional) {
        try {
          const traditional = await convertToTraditional(inputText);
          setTraditionalText(traditional);
        } catch (error) {
          console.error('繁体转换失败:', error);
        }
      }

    } catch (error) {
      console.error('Translation error:', error);
      setError(error instanceof Error ? error.message : '翻译过程中出现错误');
      setLoading(false);
    }
  };

  // 处理词语点击 - 切换展开/收起
  const handleWordClick = (word: WordInfo, element: HTMLElement | null, wordKey: string) => {
    if (!element) return;

    setActiveWord(prev => {
      // 如果点击的是当前已激活的单词，则关闭
      if (prev && prev.key === wordKey) {
        return null;
      }
      // 否则切换到新单词
      return { key: wordKey, word, element };
    });
  };

  // 处理关闭单个详情面板
  const handleClosePopover = () => {
    setActiveWord(null);
  };

  // 生成 selectedWordKeys Set
  const selectedWordKeys = new Set(activeWord ? [activeWord.key] : []);

  // ESC 键关闭所有弹出框
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeWord) {
        setActiveWord(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [activeWord]);

  return (
    <div className="app">
      <Settings isDetailOpen={!!activeWord} />
      <Toolbar
        onShowGojuon={() => setShowGojuon(true)}
        onShowSettings={() => setShowSettings(true)}
        isDetailOpen={!!activeWord}
      />
      <main className="main">
        <h1 className="page-title">中日文翻译</h1>
        <p className="helper-text">输入中文，自动翻译为日语并显示假名和罗马音</p>

        <div className="content-container">
          <TranslateInput
            value={inputText}
            onChange={setInputText}
            onSubmit={handleTranslate}
            loading={loading}
          />

          {showContent && (
            <div className={`content-expand ${loading ? 'loading' : ''}`}>
              {loading ? (
                <LoadingSkeleton />
              ) : words.length > 0 ? (
                <Result
                  words={words}
                  traditionalText={traditionalText}
                  originalText={originalText}
                  showTraditional={config.showTraditional}
                  onWordClick={handleWordClick}
                  selectedWordKeys={selectedWordKeys}
                />
              ) : null}
            </div>
          )}
        </div>

        {activeWord && (
          <WordPopover
            key={activeWord.key}
            wordInfo={activeWord.word}
            triggerElement={activeWord.element}
            onClose={handleClosePopover}
            index={0}
          />
        )}
      </main>

      {/* 五十音图 */}
      <GojuonChart
        isOpen={showGojuon}
        onClose={() => setShowGojuon(false)}
      />

      {/* 设置面板 */}
      <MobileMenu
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        title="设置"
      >
        <div className="settings-panel">
          <div className="settings-content">
            <div className="settings-section">
              <h3>显示设置</h3>
              <div className="settings-item">
                <label className="settings-label">
                  <input
                    type="checkbox"
                    checked={config.showHiragana}
                    onChange={(e) => setConfig({ ...config, showHiragana: e.target.checked })}
                  />
                  <span>显示假名标注</span>
                </label>
              </div>
              <div className="settings-item">
                <label className="settings-label">
                  <input
                    type="checkbox"
                    checked={config.showRomaji}
                    onChange={(e) => setConfig({ ...config, showRomaji: e.target.checked })}
                  />
                  <span>显示罗马音</span>
                </label>
              </div>
              <div className="settings-item">
                <label className="settings-label">
                  <input
                    type="checkbox"
                    checked={config.showTraditional}
                    onChange={(e) => setConfig({ ...config, showTraditional: e.target.checked })}
                  />
                  <span>显示繁体中文</span>
                </label>
              </div>
            </div>

            <div className="settings-section">
              <h3>字体大小</h3>
              <div className="settings-item">
                <div className="font-size-slider">
                  <input
                    type="range"
                    min="12"
                    max="24"
                    value={config.fontSize}
                    onChange={(e) => setConfig({ ...config, fontSize: Number(e.target.value) })}
                    className="size-slider"
                    style={{ '--value': `${((config.fontSize - 12) / 12) * 100}%` } as React.CSSProperties}
                  />
                  <span className="font-size-value">{config.fontSize}px</span>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>主题设置</h3>
              <div className="theme-switch">
                <button
                  className={`theme-button ${config.theme === 'light' ? 'active' : ''}`}
                  onClick={() => setConfig({ ...config, theme: 'light' })}
                >
                  <IconSvg name="ph:sun" width={16} height={16} />
                  <span>浅色</span>
                </button>
                <button
                  className={`theme-button ${config.theme === 'dark' ? 'active' : ''}`}
                  onClick={() => setConfig({ ...config, theme: 'dark' })}
                >
                  <IconSvg name="ph:moon-stars" width={16} height={16} />
                  <span>深色</span>
                </button>
              </div>
            </div>

            <div className="settings-section">
              <h3>翻译服务</h3>
              <div className="settings-item">
                <TranslateServiceContent
                  service={japaneseConfig.translateService}
                  onChange={(service: TranslateService) => updateJapaneseConfig({ translateService: service })}
                />
              </div>
            </div>
          </div>

          <div className="settings-footer">
            <button
              className="reset-button"
              onClick={() => {
                resetConfig();
                resetJapaneseConfig();
                setShowSettings(false);
              }}
            >
              重置所有设置
            </button>
          </div>
        </div>
      </MobileMenu>

      <Toast
        open={!!error}
        onOpenChange={() => setError(null)}
        title="翻译失败"
        description={error || undefined}
        type="error"
      />

      {loading && initProgress < 100 && (
        <LoadingProgress
          progress={initProgress}
          message={initMessage}
        />
      )}
    </div>
  );
};

// 主 App 组件
export const App: React.FC = () => {
  React.useEffect(() => {
    // 从 localStorage 加载配置
    const savedConfig = localStorage.getItem('jp_toolkit_config');

    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        // 设置主题
        document.documentElement.setAttribute('data-theme', config.theme);
      } catch (error) {
        console.error('Failed to parse saved config:', error);
      }
    } else {
      // 如果没有保存的配置，使用系统主题
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme = prefersDark ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', initialTheme);
    }
  }, []);

  return (
    <ConfigProvider>
      <ConfigProviderJapanese>
        <AppContent />
      </ConfigProviderJapanese>
    </ConfigProvider>
  );
};